import { useState } from 'react';
import { RiskAnalysisSection } from '../risk-analysis.client.type';

export const useRiskAnalysisFilters = (
  creators: { value: string; label: string }[],
  projectOptions: { value: string; label: string }[],
  taskTypes: { value: string; label: string }[],
  riskCategories: { value: string; label: string }[],
  repositoryOptions: { value: string; label: string }[],
  epicProjectOptions: { value: string; label: string }[] = [],
  epicRiskCategories: { value: string; label: string }[] = [],
  bloomfilterStageOptions: { value: string; label: string }[] = [],
) => {
  // Task filters
  const [creatorFilters, setCreatorFilters] = useState<string[]>([]);
  const [projectFilters, setProjectFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [epicFilters, setEpicFilters] = useState<string[]>([]);
  const [initiativeFilters, setInitiativeFilters] = useState<string[]>([]);
  const [riskCategoryFilters, setRiskCategoryFilters] = useState<string[]>([]);
  const [bloomfilterStageFilters, setBloomfilterStageFilters] = useState<string[]>([]);
  const [includeDone, setIncludeDone] = useState(true);

  // Initiative filters
  const [activeInitiativeFilter, setActiveInitiativeFilter] = useState<string>('active');
  const [initiativeRiskCategoryFilters, setInitiativeRiskCategoryFilters] = useState<string[]>([]);
  const [includeDoneInitiatives, setIncludeDoneInitiatives] = useState(true);

  // Epic filters
  const [epicProjectFilters, setEpicProjectFilters] = useState<string[]>([]);
  const [epicInitiativeFilters, setEpicInitiativeFilters] = useState<string[]>([]);
  const [epicRiskCategoryFilters, setEpicRiskCategoryFilters] = useState<string[]>([]);
  const [includeDoneEpics, setIncludeDoneEpics] = useState(true);

  // Pull Request filters
  const [prRepositoryFilters, setPrRepositoryFilters] = useState<string[]>([]);
  const [prStateFilters, setPrStateFilters] = useState<string[]>(['open', 'closed']);
  const [prRiskCategoryFilters, setPrRiskCategoryFilters] = useState<string[]>([]);

  // Flora (Insights) filters - default to all options selected
  const [floraInsightTypeFilters, setFloraInsightTypeFilters] = useState<string[]>([
    'task',
    'epic',
    'initiative',
    'pull_request',
  ]);
  const [floraSeverityFilters, setFloraSeverityFilters] = useState<string[]>(['high', 'medium', 'low']);
  const [floraStatusFilters, setFloraStatusFilters] = useState<string[]>(['not_started', 'in_progress', 'completed']);
  const [floraViewFilter, setFloraViewFilter] = useState<string>('active'); // active, dismissed, inactive

  // Objective filters
  const [objectiveStatusFilters, setObjectiveStatusFilters] = useState<string[]>([
    'on_track',
    'at_risk',
    'off_track',
    'unknown',
  ]);
  const [objectiveRiskCategoryFilters, setObjectiveRiskCategoryFilters] = useState<string[]>([]);

  const handleResetFilters = (selectedSection: RiskAnalysisSection) => {
    if (selectedSection === RiskAnalysisSection.TASKS) {
      setCreatorFilters(creators.map((creator) => creator.value));
      setProjectFilters(projectOptions.map((project) => project.value));
      setTypeFilters(taskTypes.map((type) => type.value));
      setEpicFilters([]);
      setInitiativeFilters([]);
      setRiskCategoryFilters(riskCategories.map((riskCategory) => riskCategory.value));
      setBloomfilterStageFilters(bloomfilterStageOptions.map((s) => s.value));
      setIncludeDone(true);
    } else if (selectedSection === RiskAnalysisSection.INITIATIVES) {
      setActiveInitiativeFilter('active');
      setInitiativeRiskCategoryFilters(riskCategories.map((riskCategory) => riskCategory.value));
      setIncludeDoneInitiatives(true);
    } else if (selectedSection === RiskAnalysisSection.EPICS) {
      setEpicProjectFilters(epicProjectOptions.map((project) => project.value));
      setEpicInitiativeFilters([]);
      setEpicRiskCategoryFilters(epicRiskCategories.map((riskCategory) => riskCategory.value));
      setIncludeDoneEpics(true);
    } else if (selectedSection === RiskAnalysisSection.PULL_REQUESTS) {
      setPrRepositoryFilters(repositoryOptions.map((repository) => repository.value));
      setPrStateFilters(['open', 'closed']);
      setPrRiskCategoryFilters(riskCategories.map((riskCategory) => riskCategory.value));
    } else if (selectedSection === RiskAnalysisSection.OUTCOMES) {
      setObjectiveStatusFilters(['on_track', 'at_risk', 'off_track', 'unknown']);
      setObjectiveRiskCategoryFilters(riskCategories.map((riskCategory) => riskCategory.value));
    }
  };

  /**
   * Returns every entity-scoped filter to its pre-seed empty state ("show
   * all") so the one-time initialization effect can re-seed each section
   * from fresh data. Used on portfolio switches, where selections derived
   * from the previous portfolio's data (project names, raw work-item types)
   * would otherwise persist as stale values that match nothing. Flora
   * filters are untouched — their option domains are static enums.
   *
   * @returns {void}
   */
  const handleClearAllFilters = () => {
    setCreatorFilters([]);
    setProjectFilters([]);
    setTypeFilters([]);
    setEpicFilters([]);
    setInitiativeFilters([]);
    setRiskCategoryFilters([]);
    setBloomfilterStageFilters([]);
    setIncludeDone(true);
    setActiveInitiativeFilter('active');
    setInitiativeRiskCategoryFilters([]);
    setIncludeDoneInitiatives(true);
    setEpicProjectFilters([]);
    setEpicInitiativeFilters([]);
    setEpicRiskCategoryFilters([]);
    setIncludeDoneEpics(true);
    setPrRepositoryFilters([]);
    setPrStateFilters([]);
    setPrRiskCategoryFilters([]);
    setObjectiveStatusFilters([]);
    setObjectiveRiskCategoryFilters([]);
  };

  return {
    // Task filters
    creatorFilters,
    setCreatorFilters,
    projectFilters,
    setProjectFilters,
    typeFilters,
    setTypeFilters,
    epicFilters,
    setEpicFilters,
    initiativeFilters,
    setInitiativeFilters,
    riskCategoryFilters,
    setRiskCategoryFilters,
    bloomfilterStageFilters,
    setBloomfilterStageFilters,
    includeDone,
    setIncludeDone,

    // Initiative filters
    activeInitiativeFilter,
    setActiveInitiativeFilter,
    initiativeRiskCategoryFilters,
    setInitiativeRiskCategoryFilters,
    includeDoneInitiatives,
    setIncludeDoneInitiatives,

    // Epic filters
    epicProjectFilters,
    setEpicProjectFilters,
    epicInitiativeFilters,
    setEpicInitiativeFilters,
    epicRiskCategoryFilters,
    setEpicRiskCategoryFilters,
    includeDoneEpics,
    setIncludeDoneEpics,

    // Pull Request filters
    prRepositoryFilters,
    setPrRepositoryFilters,
    prStateFilters,
    setPrStateFilters,
    prRiskCategoryFilters,
    setPrRiskCategoryFilters,

    // Flora (Insights) filters
    floraInsightTypeFilters,
    setFloraInsightTypeFilters,
    floraSeverityFilters,
    setFloraSeverityFilters,
    floraStatusFilters,
    setFloraStatusFilters,
    floraViewFilter,
    setFloraViewFilter,

    // Objective filters
    objectiveStatusFilters,
    setObjectiveStatusFilters,
    objectiveRiskCategoryFilters,
    setObjectiveRiskCategoryFilters,

    // Reset handlers
    handleResetFilters,
    handleClearAllFilters,
  };
};
